/// <amd-module name="Wit/WorkItemForm/Links/Controls/LinksGrid" />
import * as React from "react";
import { useMemo, useState } from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { Icon } from "VSSUI/Icon";
import { format } from "VSS/Core/Util/String";
import { ColumnSorting, ITableColumn, SimpleTableCell, SortOrder } from "VSSUI/Table";
import { ExpandableTreeCell, ITreeColumn, Tree } from "VSSUI/TreeEx";
import { ITreeItem, ITreeItemEx, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { FieldValue } from "Wit/WorkItemForm/Common/Components";
import { useWorkItem } from "Wit/WorkItemForm/Common/Hooks";
import * as Resources from "Wit/WorkItemForm/Resources";
import { LinkTypeState, LinkTypeUpdateDate } from "Wit/WorkItemLinksRegistration/Components";
import { LinkTypeFieldIds } from "Wit/WorkItemLinksRegistration/Constants";
import { ILinkIdentifier, IResolvedLinkDisplayData } from "Wit/WorkItemLinksRegistration/Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { ArtifactLink } from "./ArtifactLink";
import { ArtifactLinkPlaceholder } from "./ArtifactLinkPlaceholder";
import { LinksMoreButton, LinksMoreEditAction } from "./LinksMoreButton";
import { sortByNumberValue, sortByStringValue } from "Wit/Common/WorkItemLinksService/CompositeComparer";

const linkCommentPseudoRefName = "System.Links.Comment";

const defaultColumns = [CoreFieldRefNames.State, CoreFieldRefNames.ChangedDate, linkCommentPseudoRefName];

export interface ILinksGridProps {
    links: (IResolvedLinkDisplayData | undefined)[];
    onEditLink: (linkIdentifier: ILinkIdentifier) => void;
    onDeleteLink: (linkIdentifier: ILinkIdentifier) => void;
    columns?: string[];
}

/**
 * We want a class rather than an interface so that we can check instanceof at runtime
 */
class LinkTypePlaceholder {
    constructor(public linkType: string, public witLinkTypeEnd: string | undefined, public displayName: string) {}
}

class LinkGroupPlaceholder {
    constructor(public id: string, public displayName: string, public iconName?: string) {}
}

/**
 * This class holds no data - it is just here so that we can check instanceof to see what kind of row we need
 */
class LinkPlaceholder {}

/**
 * Each row in the links grid can be either
 * - A link type - the top level of the hierarchy
 * - A link group - an intermediate level in the hierarchy
 * - A link placeholder - a placeholder representing a link that has not loaded yet
 * - Link data - the actual data objects we're interested in
 */
type LinksGridItem = LinkTypePlaceholder | LinkGroupPlaceholder | LinkPlaceholder | IResolvedLinkDisplayData;

export function LinksGrid({ links, onEditLink, onDeleteLink, columns }: ILinksGridProps) {
    const [sortColumnIndex, setSortColumnIndex] = useState(0);
    const [sortOrder, setSortOrder] = useState(SortOrder.ascending);
    const workItem = useWorkItem();

    const linksProvider = getLinksTreeItemProvider(links, sortColumnIndex, sortOrder);
    const treeColumns = useMemo(
        () => getLinksGridColumns(columns ?? defaultColumns, workItem, onEditLink, onDeleteLink, sortColumnIndex, sortOrder),
        [columns, workItem, onEditLink, onDeleteLink, sortColumnIndex, sortOrder]
    );
    const sortingBehavior = useMemo(() => getSortingBehavior(setSortColumnIndex, setSortOrder), []);

    return (
        <Tree
            className="work-item-links-grid"
            behaviors={[sortingBehavior]}
            columns={treeColumns}
            itemProvider={linksProvider}
            onToggle={(e, treeItem) => {
                linksProvider.toggle(treeItem.underlyingItem);
            }}
        />
    );
}

function getLinksTreeItemProvider(
    links: (IResolvedLinkDisplayData | undefined)[],
    sortColumnIndex: number,
    sortOrder: SortOrder
): TreeItemProvider<LinksGridItem> {
    const baseComparator = sortFunctions[sortColumnIndex];
    const multiplier = sortOrder === SortOrder.ascending ? 1 : -1;
    // Sort a copy to avoid modifying one of the props!
    const sortedLinks = [...links].sort((a, b) => multiplier * baseComparator(a, b));

    const rootItems: ITreeItem<LinksGridItem>[] = [];

    for (const link of sortedLinks) {
        if (!link) {
            rootItems.push({ data: new LinkPlaceholder() });
            continue;
        }

        // Each root item corresponds to a link type
        let matchingRootItem = rootItems.find(
            item =>
                item.data instanceof LinkTypePlaceholder &&
                item.data.linkType === link.registeredLinkType &&
                item.data.witLinkTypeEnd === link.witLinkTypeEnd
        );

        if (!matchingRootItem) {
            matchingRootItem = {
                data: new LinkTypePlaceholder(link.registeredLinkType, link.witLinkTypeEnd, link.linkTypeDisplayName),
                expanded: true,
                childItems: []
            };

            rootItems.push(matchingRootItem);
        }

        if (!link.group) {
            // This link is not part of a group - put it directly under the link type root item
            matchingRootItem.childItems!.push({ data: link });
            continue;
        }

        // Each group is a item in the tree in between the root (the link type) and the leav (the link itself)
        let matchingGroup = matchingRootItem.childItems!.find(g => g.data instanceof LinkGroupPlaceholder && g.data.id === link.group!.id);

        if (!matchingGroup) {
            matchingGroup = {
                data: new LinkGroupPlaceholder(link.group.id, link.group.displayName, link.group.iconName),
                expanded: true,
                childItems: []
            };
            matchingRootItem.childItems!.push(matchingGroup);
        }

        matchingGroup.childItems!.push({ data: link });
    }

    // Always show the groups in the same order - alphabetical by link type display name
    rootItems.sort(sortByStringValue<ITreeItem<LinksGridItem>>(valueSource => (valueSource?.data as LinkTypePlaceholder).displayName));
    return new TreeItemProvider(rootItems);
}

function getLinksGridColumns(
    columns: string[],
    workItem: IWorkItem,
    onEditLink: (linkIdentifier: ILinkIdentifier) => void,
    onDeleteLink: (linkIdentifier: ILinkIdentifier) => void,
    sortColumnIndex: number,
    sortOrder: SortOrder
): ITreeColumn<LinksGridItem>[] {
    return [
        {
            id: "description",
            name: Resources.LinkDescriptionColumn,
            readonly: true,
            hierarchical: true,
            sortProps: {
                ariaLabelAscending: format(Resources.SortByNameAsc, Resources.LinkDescriptionColumn),
                ariaLabelDescending: format(Resources.SortByNameDesc, Resources.LinkDescriptionColumn),
                sortOrder: sortColumnIndex === 0 ? sortOrder : undefined
            },
            renderCell: (iRow, iCol, col, treeItem, ariaRow) => {
                const item = treeItem.underlyingItem.data;
                return (
                    <ExpandableTreeCell className={col.className} key={iCol} columnIndex={iCol} treeItem={treeItem} treeColumn={col}>
                        {item instanceof LinkTypePlaceholder ? (
                            item.displayName
                        ) : item instanceof LinkGroupPlaceholder ? (
                            <div className="artifact-link-cell flex-row">
                                <Icon className="icon-margin" iconName={item.iconName} />
                                {item.displayName}
                            </div>
                        ) : item instanceof LinkPlaceholder ? (
                            <div className="artifact-link-cell margin-left-16">
                                <ArtifactLinkPlaceholder showIcon />
                            </div>
                        ) : (
                            <div className="artifact-link-cell flex-row flex-center justify-space-between text-ellipsis">
                                <ArtifactLink artifactData={item} />
                                <LinksMoreButton
                                    className="work-item-links-more-button"
                                    onEditLink={() => onEditLink(item.linkIdentifier)}
                                    onDeleteLink={() => onDeleteLink(item.linkIdentifier)}
                                    editAction={
                                        item.linkIdentifier.FldID === LinkTypeFieldIds.WorkItem
                                            ? LinksMoreEditAction.EditLink
                                            : LinksMoreEditAction.EditComment
                                    }
                                />
                            </div>
                        )}
                    </ExpandableTreeCell>
                );
            },
            width: new ObservableValue(-3 / 8),
            onSize: onSize
        },
        ...columns.map((columnName, curIndex) => ({
            id: columnName,
            name: getColumnHeader(columnName, workItem),
            readonly: true,
            sortProps: {
                ariaLabelAscending: format(Resources.SortByNameAsc, getColumnHeader(columnName, workItem)),
                ariaLabelDescending: format(Resources.SortByNameDesc, getColumnHeader(columnName, workItem)),
                sortOrder: sortColumnIndex - 1 === curIndex ? sortOrder : undefined
            },
            renderCell: (iRow: number, iCol: number, col: ITableColumn<ITreeItemEx<LinksGridItem>>, treeItem: ITreeItemEx<LinksGridItem>) => (
                <LinksTableCell column={col} columnIndex={iCol} columnName={columnName} key={`links-${iRow}-${iCol}`} treeItem={treeItem} />
            ),
            width: new ObservableValue(-1 / 8),
            onSize: onSize
        }))
    ];
}

function onSize(event: MouseEvent, index: number, width: number, column: ITreeColumn<LinksGridItem>) {
    (column.width as ObservableValue<number>).value = width;
}

function getColumnHeader(columnName: string, workItem: IWorkItem) {
    switch (columnName) {
        case CoreFieldRefNames.State:
            return Resources.LinkStateColumn;
        case CoreFieldRefNames.ChangedDate:
            return Resources.LinkLatestUpdateColumn;
        case linkCommentPseudoRefName:
            return Resources.LinkCommentsColumn;
        default:
            return workItem.getFieldByName(columnName)?.fieldDefinition.name ?? columnName;
    }
}

interface ILinksTableCellProps {
    columnIndex: number;
    column: ITableColumn<ITreeItemEx<LinksGridItem>>;
    treeItem: ITreeItemEx<LinksGridItem>;
    columnName: string;
}

function LinksTableCell({ columnIndex, column, treeItem, columnName }: ILinksTableCellProps) {
    const item = treeItem.underlyingItem.data;

    return (
        <SimpleTableCell className={column.className} tableColumn={column} columnIndex={columnIndex}>
            {item instanceof LinkTypePlaceholder || item instanceof LinkGroupPlaceholder ? null : item instanceof LinkPlaceholder ? (
                <ArtifactLinkPlaceholder />
            ) : (
                <LinkCellValue item={item} columnName={columnName} />
            )}
        </SimpleTableCell>
    );
}

function LinkCellValue({ columnName, item }: { columnName: string; item: IResolvedLinkDisplayData }): JSX.Element | null {
    switch (columnName) {
        case CoreFieldRefNames.State:
            return <LinkTypeState link={item} />;
        case CoreFieldRefNames.ChangedDate:
            return <LinkTypeUpdateDate link={item} />;
        case linkCommentPseudoRefName:
            return <>{item.linkIdentifier.Comment}</>;
        default:
            const workItem = item.additionalData?.workItem;
            if (workItem && typeof workItem === "object") {
                const fieldValue =
                    "getFieldValueByName" in workItem
                        ? (workItem as IWorkItem).getFieldValueByName(columnName)
                        : (workItem as any).fields?.[columnName];
                return <FieldValue refName={columnName} fieldValue={fieldValue} />;
            } else {
                return null;
            }
    }
}

// Note: We're returning `any` here because getting the typing for Table behaviors to match up with the
// typing for Tree columns was a nightmare. And the typings aren't needed in this case, since
// we're not using the column typings - we're just sorting the source data when the callback is called.
function getSortingBehavior(setSortColumnIndex: (i: number) => void, setSortOrder: (so: SortOrder) => void): any {
    return new ColumnSorting<ITreeItemEx<IResolvedLinkDisplayData | LinkTypePlaceholder>>((columnIndex: number, proposedSortOrder: SortOrder) => {
        // The `sortItems` function from the Table module doesn't play nicely with the typings for Tree columns,
        // so we have to do the sorting ourselves. Here, just set the sort order state on the component. The
        // item provider will take care of the actual sorting when it changes.
        setSortColumnIndex(columnIndex);
        setSortOrder(proposedSortOrder);
    });
}

const sortFunctions = [
    // Sort on description column
    (item1: IResolvedLinkDisplayData | undefined, item2: IResolvedLinkDisplayData | undefined): number =>
        sortByStringValue<IResolvedLinkDisplayData>(linkData => linkData?.title)(item1, item2),

    // Sort on state column
    (item1: IResolvedLinkDisplayData | undefined, item2: IResolvedLinkDisplayData | undefined): number =>
        sortByStringValue<IResolvedLinkDisplayData>(linkData => linkData?.state)(item1, item2),

    // Sort on updateDate column
    (item1: IResolvedLinkDisplayData | undefined, item2: IResolvedLinkDisplayData | undefined): number =>
        sortByNumberValue<IResolvedLinkDisplayData>(linkData => linkData?.updateDate?.getTime() ?? 0)(item1, item2),

    // Sort on comment column
    (item1: IResolvedLinkDisplayData | undefined, item2: IResolvedLinkDisplayData | undefined): number =>
        sortByStringValue<IResolvedLinkDisplayData>(linkData => linkData?.linkIdentifier.Comment)(item1, item2)
];
